import { useContext, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Tab } from '@/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ProductCatalog from '@/components/ProductCatalog';
import TabItem from '@/components/tabs/TabItem';
import { useTabItemActions } from '@/hooks/tabs/useTabItemActions';
import { useIsTouch } from '@/hooks/use-touch';
import { useStagedProductsStore } from '@/stores/stagedProductsStore';
import { useTabsUIStore } from '@/stores/tabsUIStore';
import { LanguageProviderContext } from '@/providers/LanguageProvider';

interface EditExternalOrderModalProps {
  order: Tab;
  isOpen: boolean;
  onClose: () => void;
}

export const EditExternalOrderModal = ({ order, isOpen, onClose }: EditExternalOrderModalProps) => {
  const { translator } = useContext(LanguageProviderContext);
  const isTouch = useIsTouch();
  const [mode, setMode] = useState<'items' | 'add'>('items');
  const { updateItemQuantity, updateItemWeight, updatingItemId } = useTabItemActions();
  const itemDiscountInputs = useTabsUIStore((s) => s.itemDiscountInputs);
  const setItemDiscountInputs = useTabsUIStore((s) => s.setItemDiscountInputs);
  const clearStagedProducts = useStagedProductsStore((s) => s.clearStagedProducts);

  const handleClose = () => {
    clearStagedProducts();
    onClose();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) return;
    if (mode === 'add') {
      clearStagedProducts();
      setMode('items');
      return;
    }
    handleClose();
  };

  const handleCloseAdd = () => {
    clearStagedProducts();
    setMode('items');
  };

  const items = order.items ?? [];

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        {...(isTouch ? { onOpenAutoFocus: (e) => e.preventDefault() } : {})}
        className={
          mode === 'add'
            ? 'flex h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-none flex-col overflow-hidden p-4 lg:p-6 2xl:h-[85vh] 2xl:max-w-7xl'
            : 'flex max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-2xl flex-col overflow-hidden'
        }
      >
        <DialogHeader className="mb-2 shrink-0">
          <DialogTitle>{translator('ExternalOrders.editOrder')}</DialogTitle>
          <DialogDescription>
            {order.customerName}
          </DialogDescription>
        </DialogHeader>

        {mode === 'add' ? (
          <ProductCatalog
            selectedTab={order}
            onCloseClick={handleCloseAdd}
            onPayClick={handleCloseAdd}
          />
        ) : (
          <>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
              {items.length > 0 ? (
                items.map((item) => (
                  <TabItem
                    key={item.id}
                    item={item}
                    onUpdateQuantity={(itemId, quantity) => updateItemQuantity(itemId, quantity, order)}
                    onUpdateWeight={(itemId, weightKg) => updateItemWeight(itemId, weightKg, order)}
                    discountInputs={itemDiscountInputs}
                    setDiscountInputs={setItemDiscountInputs}
                    isUpdatingQuantity={updatingItemId === item.id}
                  />
                ))
              ) : (
                <p className="py-8 text-center text-muted-foreground">
                  {translator('Tabs.noItemsInTab')}
                </p>
              )}
            </div>
            <div className="shrink-0 pt-2">
              <Button type="button" onClick={() => setMode('add')} className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                {translator('Tabs.addItem')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
